'use client';
/**
 * Portal's binding of the shared @owlcat/app-shell <AppSidebar> (same component
 * habits uses, so the two apps match). Builds the generic item shape from the
 * portal nav config (tools + family/admin as the secondary group), supplies the
 * portal footer (profile cluster + support + legal) and NavLink, and matches the
 * full pathname (portal hrefs are locale-prefixed, so activePath = pathname).
 */
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { Shield, ShieldCheck, Sparkles } from 'lucide-react';
import { AppSidebar, type SidebarNavItem } from '@owlcat/app-shell';
import { NavLink } from '@/shared/components/NavLink';
import { useCurrentUser } from '@/contexts/authentication/client';
import { getTools, getFamily } from '@/contexts/application/config/navigation';
import type { NavigationItem } from '@/shared/types/navigation';
import { SidebarFooter } from './SidebarFooter';
import { PortalHeaderControls } from './PortalHeaderControls';

/**
 * Screens that ARE the visit: opened from a link in a message, with one thing
 * to do. The rail stays reachable but folded, so the page is not competing
 * with a menu. Anyone who unfolds it keeps it unfolded (AppSidebar remembers).
 */
const COLLAPSED_ROUTES = [/\/stories\/[^/]+\/ready$/];

export function PortalSidebar({ defaultCollapsed = false }: { defaultCollapsed?: boolean } = {}) {
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations();
  const { user, isAuthenticated } = useCurrentUser();
  const isAdmin = isAuthenticated && user?.role === 'admin';

  const toItem = (i: NavigationItem, secondary: boolean, size: string): SidebarNavItem => {
    const Icon = i.icon;
    return {
      id: i.id,
      label: t(i.name),
      href: `/${locale}${i.href}`,
      icon: <Icon className={size} />,
      secondary,
    };
  };

  // Gallery is now part of getTools() (the nav config) so the sidebar and the
  // dashboard tiles read from ONE source and stay in sync.
  const items: SidebarNavItem[] = [
    ...getTools().map((i) => toItem(i, false, 'h-5 w-5')),
    ...(isAuthenticated ? getFamily().map((i) => toItem(i, true, 'h-4 w-4')) : []),
  ];
  if (isAdmin) {
    items.push({
      id: 'admin',
      label: t('common.adminPanel'),
      href: `/${locale}/admin`,
      icon: <Shield className="h-4 w-4" />,
      secondary: true,
    });
    // the fleet admin (accounts: users, billing, subscriptions, comps) — the same entry in every app
    items.push({
      id: 'accounts-admin',
      label: t('common.accountsAdminPanel'),
      href: `${(process.env.NEXT_PUBLIC_ACCOUNTS_URL || 'http://127.0.0.1:3200').replace(/\/+$/, '')}/admin`,
      icon: <ShieldCheck className="h-4 w-4" />,
      secondary: true,
    });
  }

  return (
    <AppSidebar
      brand={{ label: 'OwlCat Club', emoji: '🦉🐱', href: `/${locale}` }}
      items={items}
      // The ONE primary action (same filled CTA slot film uses for "New film"):
      // authed users get "Create a story" above the nav; anon keeps the plain nav.
      cta={
        isAuthenticated
          ? {
              id: 'create',
              label: t('common.createStory'),
              href: `/${locale}/create`,
              icon: <Sparkles className="h-4 w-4" />,
            }
          : undefined
      }
      activePath={pathname}
      linkComponent={NavLink}
      footer={(close) => <SidebarFooter onCloseMobileMenu={close} />}
      collapsedFooter={<PortalHeaderControls compact />}
      // A screen that IS the visit (a finished book opened from a link) asks
      // for the rail folded; the person's own choice, once made, wins.
      defaultCollapsed={defaultCollapsed || COLLAPSED_ROUTES.some((r) => r.test(pathname))}
      collapseLabel={t('common.menu')}
    />
  );
}
